import React from 'react';
import { Award, Target, Heart, Users, BookOpen, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { founderInfo } from '../data/mock';
import SEO from '../components/SEO';
import { seoMetadata, structuredData } from '../data/seo';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title={seoMetadata.about.title}
        description={seoMetadata.about.description}
        keywords={seoMetadata.about.keywords}
        canonical={seoMetadata.about.canonical}
        additionalStructuredData={structuredData.breadcrumb('About Us', '/about')}
      />
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1728016280671-3c0ce314d744?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBpbnRlcmlvciUyMHNob3dyb29tJTIwdGlsZXMlMjBiYXRocm9vbSUyMG1vZGVybnxlbnwwfHx8fDE3NzExNDg2Mjd8MA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Us</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-200">
            Building Trust Through Quality & Expertise
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8 text-center">
              <div className="inline-block px-4 py-2 bg-red-100 text-red-600 rounded-full mb-4 font-medium">
                Meet Our Founder
              </div>
            </div>
            
            <Card className="border-0 shadow-2xl bg-white overflow-hidden">
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  {/* Left: Content */}
                  <div className="flex-1">
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">
                      {founderInfo.name}
                    </h2>
                    
                    <p className="text-xl text-red-600 font-semibold mb-6">
                      {founderInfo.tagline}
                    </p>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-start space-x-3">
                        <Award className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900">{founderInfo.qualification}</p>
                          <p className="text-gray-600 text-sm">Academic Excellence</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-3">
                        <Target className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900">{founderInfo.experience}</p>
                          <p className="text-gray-600 text-sm">Industry Experience</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-4">
                      {founderInfo.bio}
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      With a deep understanding of construction materials and interior design, 
                      he founded Bombay Home Gallery to solve the most common challenges 
                      faced by homeowners in Lucknow – finding quality products at fair prices with 
                      expert guidance.
                    </p>

                    <div className="mt-6 p-4 bg-red-50 rounded-xl">
                      <Link to="/ebook" className="flex items-center justify-between group">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Download Our Premium Homebuilding Guide</p>
                            <p className="text-sm text-gray-600">Learn smart homebuilding from an expert</p>
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-red-600 group-hover:translate-x-2 transition-transform" />
                      </Link>
                    </div>
                  </div>

                  {/* Right: Image */}
                  <div className="md:w-64 flex-shrink-0">
                    <div className="relative">
                      <img
                        src={founderInfo.image}
                        alt={founderInfo.name}
                        className="w-full h-auto rounded-2xl shadow-xl object-cover object-top"
                        style={{ maxHeight: '400px' }}
                      />
                      <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-red-600 rounded-full opacity-20 blur-2xl" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Story
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Bombay Home Gallery was born from a simple vision: to create a one-stop destination 
              where homeowners, contractors, and interior designers could find premium quality 
              building materials with professional guidance.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Located in the heart of Lucknow, we've built our reputation on three pillars: 
              quality branded products, competitive pricing, and expert technical consultation. 
              Every product in our showroom is carefully selected to meet the highest standards.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              Today, we're proud to be Lucknow's most trusted name in tiles, bathroom solutions, 
              and plumbing essentials – serving thousands of happy customers who've made their 
              dream homes a reality.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow bg-white p-6">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Quality First</h3>
                <p className="text-gray-600">
                  Only branded and certified products that meet the highest standards
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow bg-white p-6">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Trust</h3>
                <p className="text-gray-600">
                  Building lasting relationships through transparency and honesty
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow bg-white p-6">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Guidance</h3>
                <p className="text-gray-600">
                  Professional consultation backed by engineering expertise
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow bg-white p-6">
              <CardContent className="p-0">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fair Pricing</h3>
                <p className="text-gray-600">
                  Competitive rates without compromising on quality
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Brand Positioning */}
      <section className="py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why We're Different
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">Professional</div>
                <p className="text-red-100">
                  Engineering expertise backed by formal education and experience
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">Luxurious</div>
                <p className="text-red-100">
                  Premium products at prices that don't break the bank
                </p>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">Trust Driven</div>
                <p className="text-red-100">
                  Your satisfaction is our reputation in Lucknow
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
